import { useState, useRef } from "react";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Printer, FileDown } from "lucide-react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { jsPDF } from "jspdf";

// Campos disponíveis para etiquetas
const CAMPOS_DISPONIVEIS: Record<string, string> = {
  cod: "Código do Produto",
  descricao: "Descrição",
  ncm: "NCM",
  eanUnit: "EAN Unitário",
  eanCaixa: "EAN Caixa",
  unidadeFiscal: "Unidade Fiscal",
  unidadeComercial: "Unidade Comercial",
  qtdCaixaMaster: "Qtd Caixa Master",
  altura: "Altura (cm)",
  largura: "Largura (cm)",
  comprimento: "Comprimento (cm)",
  custo: "Custo",
  venda: "Venda",
};

interface LabelElemento {
  id: string;
  tipo: "texto" | "barcode" | "qrcode";
  campo: string;
  x: number;
  y: number;
  largura: number;
  altura: number;
  fontSize?: number;
}

interface LabelTemplate {
  id: number;
  nome: string;
  larguraMm: number;
  alturaMm: number;
  elementos: LabelElemento[];
}

interface Produto {
  id: number;
  cod: string;
  descricao?: string;
  ncm?: string;
  eanUnit?: string;
  eanCaixa?: string;
  unidadeFiscal?: string;
  unidadeComercial?: string;
  qtdCaixaMaster?: number;
  altura?: number;
  largura?: number;
  comprimento?: number;
  custo?: number;
  venda?: number;
}

function getValorCampo(produto: Produto, campo: string): string {
  const val = (produto as any)[campo];
  if (val === null || val === undefined || val === "") return "";
  if (typeof val === "number") return String(val);
  return String(val);
}

async function gerarBarcodePNG(valor: string, larguraPx: number, alturaPx: number): Promise<string> {
  const canvas = document.createElement("canvas");
  try {
    JsBarcode(canvas, valor, {
      format: "CODE128",
      width: Math.max(1, larguraPx / (valor.length * 1.5 + 4)),
      height: alturaPx,
      displayValue: false,
      margin: 2,
    });
    return canvas.toDataURL("image/png");
  } catch {
    return "";
  }
}

async function gerarQRCodePNG(valor: string, tamanho: number): Promise<string> {
  try {
    return await QRCode.toDataURL(valor, { width: tamanho, margin: 1 });
  } catch {
    return "";
  }
}

async function gerarPDF(
  produto: Produto,
  template: LabelTemplate,
  quantidade: number
): Promise<void> {
  const { larguraMm, alturaMm, elementos } = template;

  // Configurar jsPDF com tamanho personalizado
  const doc = new jsPDF({
    orientation: larguraMm > alturaMm ? "landscape" : "portrait",
    unit: "mm",
    format: [larguraMm, alturaMm],
  });

  const MM_TO_PX = 3.7795; // 96 DPI

  for (let i = 0; i < quantidade; i++) {
    if (i > 0) {
      doc.addPage([larguraMm, alturaMm], larguraMm > alturaMm ? "landscape" : "portrait");
    }

    for (const el of elementos) {
      const valor = getValorCampo(produto, el.campo);
      if (!valor) continue;

      if (el.tipo === "texto") {
        doc.setFontSize(el.fontSize ?? 10);
        doc.setFont("helvetica", "normal");
        // Quebra de linha automática dentro da largura
        const linhas = doc.splitTextToSize(valor, el.largura - 1);
        doc.text(linhas, el.x + 0.5, el.y + (el.fontSize ?? 10) * 0.35, {
          baseline: "top",
          maxWidth: el.largura - 1,
        });
      } else if (el.tipo === "barcode") {
        const larguraPx = Math.round(el.largura * MM_TO_PX);
        const alturaPx = Math.round(el.altura * MM_TO_PX);
        const dataUrl = await gerarBarcodePNG(valor, larguraPx, alturaPx);
        if (dataUrl) {
          doc.addImage(dataUrl, "PNG", el.x, el.y, el.largura, el.altura);
        }
      } else if (el.tipo === "qrcode") {
        const tamanho = Math.round(Math.min(el.largura, el.altura) * MM_TO_PX);
        const dataUrl = await gerarQRCodePNG(valor, tamanho);
        if (dataUrl) {
          doc.addImage(dataUrl, "PNG", el.x, el.y, el.largura, el.altura);
        }
      }
    }
  }

  doc.save(`etiquetas_${produto.cod}_${quantidade}x.pdf`);
}

export default function ImprimirEtiquetas() {
  const { data: templates = [], isLoading: loadingTemplates } = trpc.labelTemplates.list.useQuery();
  const { data: products = [], isLoading: loadingProducts } = trpc.products.list.useQuery();

  const [produtoId, setProdutoId] = useState<string>("");
  const [templateId, setTemplateId] = useState<string>("");
  const [quantidade, setQuantidade] = useState<number>(1);
  const [gerando, setGerando] = useState(false);
  const [searchProduto, setSearchProduto] = useState("");

  const produtosFiltrados = (products as Produto[]).filter((p) => {
    if (!searchProduto.trim()) return true;
    const s = searchProduto.toLowerCase();
    return (
      p.cod.toLowerCase().includes(s) ||
      (p.descricao && p.descricao.toLowerCase().includes(s))
    );
  });

  const produtoSelecionado = (products as Produto[]).find((p) => String(p.id) === produtoId) ?? null;
  const templateSelecionado = templates.find((t) => String(t.id) === templateId) ?? null;

  async function handleGerar() {
    if (!produtoSelecionado) {
      toast.error("Selecione um produto.");
      return;
    }
    if (!templateSelecionado) {
      toast.error("Selecione um template de etiqueta.");
      return;
    }
    if (quantidade < 1) {
      toast.error("A quantidade deve ser pelo menos 1.");
      return;
    }
    if (quantidade > 500) {
      toast.error("Quantidade máxima permitida: 500 etiquetas.");
      return;
    }
    if (templateSelecionado.elementos.length === 0) {
      toast.error("O template selecionado não possui elementos. Edite-o antes de imprimir.");
      return;
    }

    setGerando(true);
    try {
      await gerarPDF(produtoSelecionado, templateSelecionado as LabelTemplate, quantidade);
      toast.success(`PDF com ${quantidade} etiqueta(s) gerado com sucesso.`);
    } catch (err: any) {
      toast.error("Erro ao gerar PDF: " + (err?.message ?? "Erro desconhecido"));
    } finally {
      setGerando(false);
    }
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Printer className="w-6 h-6" />
            Imprimir Etiquetas
          </h1>
          <p className="text-muted-foreground text-sm">Selecione o produto, template e quantidade para gerar o PDF de impressão</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Painel de configuração */}
        <Card>
          <CardHeader>
            <CardTitle>Configuração de Impressão</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Busca e seleção de produto */}
            <div className="space-y-2">
              <Label>Produto</Label>
              <Input
                placeholder="Buscar por código ou descrição..."
                value={searchProduto}
                onChange={(e) => setSearchProduto(e.target.value)}
                className="mb-1"
              />
              <Select value={produtoId} onValueChange={setProdutoId} disabled={loadingProducts}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingProducts ? "Carregando produtos..." : "Selecione o produto"} />
                </SelectTrigger>
                <SelectContent>
                  {produtosFiltrados.slice(0, 100).map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      <span className="font-mono text-xs mr-2">{p.cod}</span>
                      <span className="text-muted-foreground">{p.descricao ?? ""}</span>
                    </SelectItem>
                  ))}
                  {produtosFiltrados.length > 100 && (
                    <SelectItem value="__more__" disabled>
                      ... {produtosFiltrados.length - 100} produtos adicionais (refine a busca)
                    </SelectItem>
                  )}
                  {produtosFiltrados.length === 0 && (
                    <SelectItem value="__empty__" disabled>Nenhum produto encontrado</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Seleção de template */}
            <div className="space-y-2">
              <Label>Template de Etiqueta</Label>
              <Select value={templateId} onValueChange={setTemplateId} disabled={loadingTemplates}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingTemplates ? "Carregando templates..." : "Selecione o template"} />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 && (
                    <SelectItem value="__empty__" disabled>Nenhum template cadastrado</SelectItem>
                  )}
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.nome} — {t.larguraMm}×{t.alturaMm}mm ({t.elementos.length} elemento{t.elementos.length !== 1 ? "s" : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantidade */}
            <div className="space-y-2">
              <Label>Quantidade de Etiquetas</Label>
              <Input
                type="number"
                value={quantidade}
                onChange={(e) => setQuantidade(Math.max(1, Math.min(500, Number(e.target.value))))}
                min={1}
                max={500}
              />
              <p className="text-xs text-muted-foreground">Máximo: 500 etiquetas por PDF</p>
            </div>

            <Button
              className="w-full"
              onClick={handleGerar}
              disabled={gerando || !produtoId || !templateId || quantidade < 1}
            >
              {gerando ? (
                <>Gerando PDF...</>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Gerar PDF de Etiquetas
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Painel de preview do produto selecionado */}
        <Card>
          <CardHeader>
            <CardTitle>Dados do Produto</CardTitle>
          </CardHeader>
          <CardContent>
            {!produtoSelecionado ? (
              <p className="text-sm text-muted-foreground">Selecione um produto para visualizar seus dados.</p>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {Object.entries(CAMPOS_DISPONIVEIS).map(([campo, label]) => {
                    const valor = getValorCampo(produtoSelecionado, campo);
                    return (
                      <div key={campo} className="flex flex-col">
                        <span className="text-xs text-muted-foreground">{label}</span>
                        <span className={valor ? "font-medium" : "text-muted-foreground italic"}>
                          {valor || "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview do template selecionado */}
      {templateSelecionado && (
        <Card>
          <CardHeader>
            <CardTitle>Preview do Template: {templateSelecionado.nome}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Tamanho: {templateSelecionado.larguraMm} × {templateSelecionado.alturaMm} mm &nbsp;|&nbsp; {templateSelecionado.elementos.length} elemento(s)
            </p>
            <div className="overflow-auto">
              <div
                style={{
                  position: "relative",
                  width: templateSelecionado.larguraMm * 3.5,
                  height: templateSelecionado.alturaMm * 3.5,
                  background: "white",
                  border: "2px solid #64748b",
                  flexShrink: 0,
                }}
              >
                {/* Grid */}
                <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
                  <defs>
                    <pattern id="grid-preview" width={5 * 3.5} height={5 * 3.5} patternUnits="userSpaceOnUse">
                      <path d={`M ${5 * 3.5} 0 L 0 0 0 ${5 * 3.5}`} fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid-preview)" />
                </svg>

                {templateSelecionado.elementos.map((el) => {
                  const valor = produtoSelecionado ? getValorCampo(produtoSelecionado, el.campo) : "";
                  const campoLabel = CAMPOS_DISPONIVEIS[el.campo] ?? el.campo;
                  const exibir = valor || `[${campoLabel}]`;
                  return (
                    <div
                      key={el.id}
                      style={{
                        position: "absolute",
                        left: el.x * 3.5,
                        top: el.y * 3.5,
                        width: el.largura * 3.5,
                        height: el.altura * 3.5,
                        border: "1px dashed #94a3b8",
                        background: "rgba(255,255,255,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        borderRadius: 2,
                      }}
                    >
                      <span style={{
                        fontSize: Math.min((el.fontSize || 10) * 3.5 * 0.35, el.altura * 3.5 * 0.8),
                        color: "#334155",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        padding: "0 2px",
                      }}>
                        {el.tipo === "barcode" ? `▌▌▌▌ ${exibir}` : el.tipo === "qrcode" ? `▦ ${exibir}` : exibir}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
